import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import PageLayout from "../Layout/PageLayout";
import AdminService from "../../../api/services/adminService";
import { imageUrl } from "../../../api/apiConfig/apiConfig";
import "../Home/Homepage.css";
import "./UpdateApkPage.css";

const UpdateApkPage = () => {
  const [apkInfo, setApkInfo] = useState({
    version: "—",
    uploadedAt: null,
    apk: "",
  });
  const [apkMetaLoading, setApkMetaLoading] = useState(true);
  const [apkMetaError, setApkMetaError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [newVersion, setNewVersion] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  const APK_ACCEPT =
    ".apk,.APK,application/vnd.android.package-archive,application/octet-stream";

  const fetchCurrentApk = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) {
      setApkMetaLoading(true);
      setApkMetaError("");
    }
    try {
      const res = await AdminService.getApk();
      if (res?.success && res?.data) {
        const d = res.data;
        setApkInfo({
          version: d.version ?? "—",
          uploadedAt: d.updatedAt || d.createdAt || null,
          apk: d.apk || "",
        });
      } else if (!silent) {
        setApkMetaError(res?.message || "Could not load APK info.");
      }
    } catch (err) {
      if (!silent) {
        setApkMetaError(err?.response?.data?.message || err?.message || "Could not load APK info.");
      }
    } finally {
      if (!silent) setApkMetaLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentApk();
  }, [fetchCurrentApk]);

  const apkDownloadHref =
    apkInfo.apk && imageUrl
      ? `${String(imageUrl).replace(/\/$/, "")}/${apkInfo.apk.replace(/^\//, "")}`
      : "";

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const isApk =
        file.name.toLowerCase().endsWith(".apk") ||
        file.type === "application/vnd.android.package-archive";
      if (!isApk) {
        setUploadError("Please select a valid .apk file.");
        e.target.value = "";
        return;
      }
      setSelectedFile(file);
      setUploadSuccess(false);
      setUploadError("");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.name.toLowerCase().endsWith(".apk")) {
      setSelectedFile(file);
      setUploadSuccess(false);
      setUploadError("");
    } else if (file) {
      setUploadError("Please drop an APK file.");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploadError("");
    if (!selectedFile) {
      setUploadError("Please select an APK file to upload.");
      return;
    }
    const version = newVersion.trim() || selectedFile.name.replace(/\.apk$/i, "").replace(/[^0-9.]/g, "") || "1.0.0";
    const formData = new FormData();
    formData.append("version", version);
    formData.append("apk", selectedFile);
    setUploadLoading(true);
    try {
      const res = await AdminService.updateApk(formData);
      if (res?.success) {
        setSelectedFile(null);
        setNewVersion("");
        setUploadSuccess(true);
        fetchCurrentApk({ silent: true });
      } else {
        setUploadSuccess(false);
        setUploadError(res?.message || "Failed to upload APK.");
      }
    } catch (err) {
      setUploadSuccess(false);
      setUploadError(err?.response?.data?.message || err?.message || "Failed to upload APK.");
    } finally {
      setUploadLoading(false);
    }
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return iso || "—";
    }
  };

  return (
    <PageLayout
      title="Update Apk"
      breadcrumb={
        <Link to="/dashboard/homepage" className="dashboard-hero__breadcrumb">
          Dashboard
        </Link>
      }
    >
      <div className="update-apk-grid">
        <div className="update-apk-card update-apk-card--version">
          <h2 className="update-apk-card__title">Current APK Version</h2>
          {apkMetaLoading ? (
            <p className="update-apk-meta-hint">Loading current APK…</p>
          ) : apkMetaError ? (
            <p className="update-apk-meta-error">{apkMetaError}</p>
          ) : (
            <>
              <div className="update-apk-version-row">
                <span className="update-apk-version-label">Version</span>
                <span className="update-apk-version-value">{apkInfo.version}</span>
              </div>
              <div className="update-apk-version-row">
                <span className="update-apk-version-label">Last updated</span>
                <span className="update-apk-version-value">
                  {apkInfo.uploadedAt ? formatDate(apkInfo.uploadedAt) : "—"}
                </span>
              </div>
              {apkInfo.apk && (
                <div className="update-apk-version-row">
                  <span className="update-apk-version-label">APK file</span>
                  <div className="update-apk-file-cell">
                    
                    {apkDownloadHref && (
                      <a
                        href={apkDownloadHref}
                        className="update-apk-download"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download
                      </a>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="update-apk-card update-apk-card--upload">
          <h2 className="update-apk-card__title">Upload New APK</h2>
          <form onSubmit={handleUpload} className="update-apk-form">
            <div className={`update-apk-dropzone ${dragOver ? "update-apk-dropzone--active" : ""}`}>
              <input
                ref={fileInputRef}
                type="file"
                accept={APK_ACCEPT}
                onChange={handleFileChange}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className="update-apk-file-input"
                id="apk-file"
                aria-label="Choose APK file"
              />
              <div className="update-apk-dropzone-visual">
                {selectedFile ? (
                  <span className="update-apk-filename">{selectedFile.name}</span>
                ) : (
                  <>
                    <span className="update-apk-dropzone-icon" aria-hidden>
                      📦
                    </span>
                    <span>Drop APK here or click anywhere in this area</span>
                  </>
                )}
              </div>
            </div>
            {/* <button
              type="button"
              className="update-apk-browse-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadLoading}
            >
              Choose APK file
            </button> */}
            <div className="update-apk-field">
              <label htmlFor="apk-version" className="update-apk-field-label">
                Version (e.g. 2.1.1)
              </label>
              <input
                id="apk-version"
                type="text"
                placeholder="2.1.1"
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                className="update-apk-input"
              />
            </div>
            <button type="submit" className="update-apk-submit" disabled={uploadLoading}>
              {uploadLoading ? "Uploading..." : "Upload APK"}
            </button>
            {!!uploadError && (
              <p className="update-apk-error">{uploadError}</p>
            )}
            {uploadSuccess && (
              <p className="update-apk-success">APK uploaded successfully. Current version has been updated.</p>
            )}
          </form>
        </div>
      </div>
    </PageLayout>
  );
};

export default UpdateApkPage;
